import * as monaco from 'monaco-editor';
import { must } from './dom.ts';
import makeEditor from './editor.ts';

// The slice of the assignment's config.json the tabs care about. language
// can be absent (Monaco then treats files by extension only). files and
// pinnableMain feed the managed/protected split (below).
type TabsConfig = {
  language?: string;
  allowNewFiles?: boolean;
  files?: string[];
  pinnableMain?: boolean;
};

/*
 * Tabbed multi-file wrapper around a single Monaco editor: one model per
 * file, switched with setModel(). Undo history rides along with each model
 * and we save/restore the view state (cursor, scroll) per file. The tab bar
 * stays hidden for plain single-file assignments so those pages look exactly
 * as they did before tabs existed.
 */

const MAX_FILES = 10;

const LANGUAGES: Record<string, string> = {
  java: 'java',
  js: 'javascript',
  mjs: 'javascript',
  py: 'python',
  html: 'html',
  css: 'css',
  json: 'json',
  md: 'markdown',
};

// What we accept as the name of a student-created file, per config.language.
const FILENAMES: Record<string, { pattern: RegExp; example: string }> = {
  java: { pattern: /^[A-Z][A-Za-z0-9]*\.java$/, example: 'Example.java' },
  javascript: { pattern: /^[a-z][a-z0-9_-]*\.js$/, example: 'example.js' },
  python: { pattern: /^[a-z][a-z0-9_-]*\.py$/, example: 'example.py' },
};

// The fallback rules for a page with no declared language (Monaco keys off
// the extension alone).
const GENERIC_FILENAME = { pattern: /^[A-Za-z][A-Za-z0-9._-]*$/, example: 'file.txt' };

const language = (file: string, fallback: string | undefined) => {
  const ext = file.split('.').pop() ?? '';
  return LANGUAGES[ext] ?? fallback;
};

// The initial contents of a student-created file. All languages start empty
// for now (Java used to get a class skeleton matching the filename).
const stubFor = (_file: string) => '';

class EditorTabs {
  editor: monaco.editor.IStandaloneCodeEditor;
  editorDiv: HTMLElement;
  defaultLanguage: string | undefined;
  allowNewFiles: boolean;
  // Files the student may NOT delete or rename (plans/delete-rename-files.md):
  // an assignment's configured starters. On a pinnableMain page the one
  // configured file is just the run pin, so nothing is protected there.
  #protectedFiles: Set<string>;
  // Browse mode (viewing an old revision): the tab set is a snapshot view,
  // so file management is off.
  browsing = false;
  tabBar: HTMLElement;
  addButton: HTMLButtonElement | null = null;
  // Shown in the editor's place while a new-file-capable page has no files
  // yet, so the student isn't faced with a blank editable pane before they've
  // made anything (plans/student-projects.md). Null on pages that always
  // have files.
  emptyState: HTMLElement | null = null;
  // The inline "name your file" textbox, while one is open (only one at a time).
  newFileInput: HTMLInputElement | null = null;
  models: Map<string, monaco.editor.ITextModel> = new Map();
  viewStates: Map<string, monaco.editor.ICodeEditorViewState | null> = new Map();
  buttons: Map<string, HTMLButtonElement> = new Map();
  current: string | null = null;
  onAddFile: ((file: string) => void) | null = null;
  // Installed by the page (never while reviewing) BEFORE any tab opens: do
  // the server work for a delete/rename gesture, and on success call
  // close()/rename() and resolve true; on a handled failure show the error
  // and resolve false. The tabs only ever change after a delegate succeeds.
  onDeleteFile: ((file: string) => Promise<boolean>) | null = null;
  onRenameFile: ((from: string, to: string) => Promise<boolean>) | null = null;

  constructor(editorId: string, config: TabsConfig) {
    this.defaultLanguage = config.language;
    this.allowNewFiles = !!config.allowNewFiles;
    this.#protectedFiles = new Set(config.pinnableMain ? [] : (config.files ?? []));
    this.editor = makeEditor(editorId, config.language);

    const editorDiv = must(document.getElementById(editorId), `editor element #${editorId}`);
    this.editorDiv = editorDiv;
    let tabBar = editorDiv.parentElement?.querySelector('.editor-tabs') as HTMLElement | null;
    if (!tabBar) {
      // Pages whose template predates the .editor-pane wrapper still work;
      // the bar just gets inserted as the editor's sibling.
      tabBar = document.createElement('div');
      tabBar.className = 'editor-tabs';
      tabBar.hidden = true;
      editorDiv.insertAdjacentElement('beforebegin', tabBar);
    }
    this.tabBar = tabBar;

    if (this.allowNewFiles) {
      this.addButton = document.createElement('button');
      this.addButton.className = 'add-file';
      this.addButton.type = 'button';
      this.addButton.title = 'New file';
      this.addButton.innerText = '+';
      this.addButton.onclick = () => this.startNewFileTab();
      this.tabBar.append(this.addButton);
      // A project can start with no files at all (plans/student-projects.md):
      // then open() — the only other place that unhides the bar — never runs,
      // so reveal it here or the student would have no + button to make their
      // first file. #showOrHideTabBar keeps it shown whenever allowNewFiles.
      this.tabBar.hidden = false;

      // The empty-editor placeholder, shown until the first file exists.
      const empty = document.createElement('div');
      empty.className = 'editor-empty';
      const line1 = document.createElement('p');
      line1.className = 'editor-empty-lead';
      line1.textContent = 'No files yet.';
      const line2 = document.createElement('p');
      line2.textContent = `Click the + tab to create your first file (like ${this.#filenameRules().example}).`;
      empty.append(line1, line2);
      editorDiv.insertAdjacentElement('afterend', empty);
      this.emptyState = empty;
      this.#showOrHideEditor();
    }
  }

  /*
   * Create a tab for the file if it doesn't have one yet and set its content.
   * The first file opened becomes the selected tab.
   */
  open(file: string, content: string) {
    const existing = this.models.get(file);
    if (existing) {
      existing.setValue(content);
      return;
    }

    const model = monaco.editor.createModel(content, language(file, this.defaultLanguage));
    this.models.set(file, model);

    // The tab's current file name lives in dataset.file (handlers read it
    // there, so a rename only has to update the dataset and the label).
    const button = document.createElement('button');
    button.className = 'tab';
    button.type = 'button';
    button.dataset.file = file;
    const label = document.createElement('span');
    label.className = 'tab-label';
    label.textContent = file;
    button.append(label);
    button.onclick = () => this.select(button.dataset.file as string);
    if (this.#managed(file)) {
      if (this.onRenameFile) {
        button.title = 'Double-click to rename';
        button.ondblclick = () => this.#startRenameTab(button);
      }
      if (this.onDeleteFile) {
        const close = document.createElement('span');
        close.className = 'tab-close';
        close.textContent = '×';
        close.title = 'Delete this file';
        close.onclick = (e) => {
          e.stopPropagation();
          this.#deleteTab(button);
        };
        button.append(close);
      }
    }
    if (this.addButton) {
      this.addButton.before(button);
    } else {
      this.tabBar.append(button);
    }
    this.buttons.set(file, button);

    if (this.current === null) {
      // Replace (and dispose) the empty model the editor was created with.
      const placeholder = this.editor.getModel();
      this.select(file);
      placeholder?.dispose();
    }
    this.#showOrHideTabBar();
    this.#showOrHideEditor();
  }

  select(file: string) {
    const model = this.models.get(file);
    if (!model || file === this.current) return;

    if (this.current !== null) {
      this.viewStates.set(this.current, this.editor.saveViewState());
      this.buttons.get(this.current)?.classList.remove('active');
    }
    this.editor.setModel(model);
    const viewState = this.viewStates.get(file);
    if (viewState) {
      this.editor.restoreViewState(viewState);
    }
    this.buttons.get(file)?.classList.add('active');
    this.current = file;
    this.editor.focus();
  }

  has(file: string) {
    return this.models.has(file);
  }

  content(file: string) {
    return this.models.get(file)?.getValue() ?? '';
  }

  setContent(file: string, content: string) {
    this.models.get(file)?.setValue(content);
  }

  /*
   * Filenames in tab order (which is the order they were opened).
   */
  files(): string[] {
    return Array.from(this.models.keys());
  }

  /*
   * All files and their current contents, in tab order.
   */
  contents(): { file: string; content: string }[] {
    return this.files().map((file) => ({ file, content: this.content(file) }));
  }

  // A managed file is one the student could have created, so one they may
  // delete or rename; the assignment's own configured files are protected.
  #managed(file: string) {
    return this.allowNewFiles && !this.#protectedFiles.has(file);
  }

  /*
   * Remove a tab and its model (the mechanical half of a delete — the page's
   * delegate has already committed the removal). Selects a neighbor when the
   * closed tab was current; with no tabs left the editor gets a fresh empty
   * placeholder model (and, on new-file pages, hides behind the empty state).
   */
  close(file: string) {
    const model = this.models.get(file);
    if (!model) return;
    const files = this.files();
    const idx = files.indexOf(file);
    const neighbor = files[idx + 1] ?? files[idx - 1];
    this.buttons.get(file)?.remove();
    this.buttons.delete(file);
    this.models.delete(file);
    this.viewStates.delete(file);
    if (this.current === file) {
      this.current = null;
      if (neighbor !== undefined) {
        this.select(neighbor);
      } else {
        // Don't leave the editor holding the model we're about to dispose.
        this.editor.setModel(monaco.editor.createModel('', this.defaultLanguage));
      }
    }
    model.dispose();
    this.#showOrHideTabBar();
    this.#showOrHideEditor();
  }

  /*
   * Re-key a tab (the mechanical half of a rename — the delegate has already
   * committed it). The Monaco model survives, so undo history and view state
   * ride along; only the language updates if the extension's changed (possible
   * only on generic-filename pages).
   */
  rename(from: string, to: string) {
    const model = this.models.get(from);
    if (!model || from === to || this.models.has(to)) return;
    // Rebuild the map to keep the tab order (a Map can't re-key in place).
    this.models = new Map([...this.models].map(([f, m]) => [f === from ? to : f, m]));
    const viewState = this.viewStates.get(from);
    this.viewStates.delete(from);
    if (viewState !== undefined) this.viewStates.set(to, viewState);
    const button = this.buttons.get(from);
    this.buttons.delete(from);
    if (button) {
      this.buttons.set(to, button);
      button.dataset.file = to;
      const label = button.querySelector('.tab-label');
      if (label) label.textContent = to;
    }
    if (this.current === from) this.current = to;
    const lang = language(to, this.defaultLanguage);
    if (lang !== undefined && lang !== model.getLanguageId()) {
      monaco.editor.setModelLanguage(model, lang);
    }
  }

  /*
   * Browse mode (plans/delete-rename-files.md): the tabs show an old
   * revision's snapshot, so file management is off — no +, no ×, no rename.
   */
  setBrowsing(browsing: boolean) {
    this.browsing = browsing;
    if (this.addButton) this.addButton.disabled = browsing;
    this.tabBar.classList.toggle('browsing', browsing);
  }

  // The filename rules for the page's language (a generic fallback when the
  // page declares none).
  #filenameRules(): { pattern: RegExp; example: string } {
    return (this.defaultLanguage && FILENAMES[this.defaultLanguage]) || GENERIC_FILENAME;
  }

  // Why `name` can't be a new file, or null if it's fine.
  #newFileProblem(name: string, rules: { pattern: RegExp }): string | null {
    if (!name) return 'Enter a file name.';
    if (!rules.pattern.test(name)) return `File names look like ${this.#filenameRules().example}`;
    if (this.models.has(name)) return `${name} already exists.`;
    return null;
  }

  /*
   * Open an inline textbox as a tab, where the new file's name will go, and
   * create the file from what's typed. Enter commits (an invalid name keeps
   * the box open, flagged, so it can be fixed); Escape cancels; blurring
   * commits a valid name and otherwise just makes the box go away — so
   * clicking + and then clicking elsewhere leaves nothing behind.
   */
  startNewFileTab() {
    if (this.browsing) return;
    if (this.models.size >= MAX_FILES) {
      alert(`No more than ${MAX_FILES} files, sorry.`);
      return;
    }
    // One at a time — a second + click just refocuses the open box.
    if (this.newFileInput) {
      this.newFileInput.focus();
      return;
    }

    const rules = this.#filenameRules();
    const input = document.createElement('input');
    input.className = 'tab tab-input';
    input.type = 'text';
    input.placeholder = rules.example;
    input.setAttribute('aria-label', 'New file name');
    input.spellcheck = false;
    input.autocomplete = 'off';
    this.newFileInput = input;
    if (this.addButton) {
      this.addButton.before(input);
    } else {
      this.tabBar.append(input);
    }

    // `done` guards against the blur that fires when we remove the input
    // ourselves (on Enter/Escape) turning into a second commit/cancel.
    let done = false;
    const remove = () => {
      if (done) return;
      done = true;
      this.newFileInput = null;
      input.remove();
    };
    const commit = (): boolean => {
      if (done) return true;
      const name = input.value.trim();
      const problem = this.#newFileProblem(name, rules);
      if (problem) {
        input.classList.add('invalid');
        input.title = problem;
        return false;
      }
      done = true;
      this.newFileInput = null;
      input.remove();
      this.open(name, stubFor(name));
      this.select(name);
      this.onAddFile?.(name);
      return true;
    };

    input.onkeydown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        remove();
      } else {
        input.classList.remove('invalid');
      }
    };
    // Blur: keep a valid name, drop anything else (including an empty box).
    input.onblur = () => {
      if (!commit()) remove();
    };

    input.focus();
  }

  /*
   * The ×-click: confirm, then hand the delete to the page's delegate. The
   * tab disappears only when the delegate's commit lands (it calls close());
   * a failure leaves everything as it was, error shown by the delegate.
   */
  #deleteTab(button: HTMLButtonElement) {
    if (this.browsing || !this.onDeleteFile) return;
    const file = button.dataset.file as string;
    if (!confirm(`Delete ${file}? Old versions stay in the revision history.`)) return;
    button.disabled = true;
    // The delegate reports its own failures and never rejects, but a
    // re-enabled button on any outcome-but-success keeps a bug here from
    // wedging the tab.
    void this.onDeleteFile(file).then(
      (ok) => {
        if (!ok) button.disabled = false;
      },
      () => {
        button.disabled = false;
      },
    );
  }

  /*
   * Double-click-to-rename: the tab becomes the same inline textbox the +
   * flow uses, pre-filled. Enter commits (an invalid name keeps the box
   * open, flagged), Escape cancels, blur commits-if-valid. The tab itself
   * changes only when the delegate's commit lands (it calls rename());
   * until then the old name stands.
   */
  #startRenameTab(button: HTMLButtonElement) {
    if (this.browsing || !this.onRenameFile) return;
    // Shares the one-at-a-time slot with the + flow.
    if (this.newFileInput) {
      this.newFileInput.focus();
      return;
    }
    const from = button.dataset.file as string;
    const rules = this.#filenameRules();
    const input = document.createElement('input');
    input.className = 'tab tab-input';
    input.type = 'text';
    input.value = from;
    input.placeholder = rules.example;
    input.setAttribute('aria-label', `Rename ${from}`);
    input.spellcheck = false;
    input.autocomplete = 'off';
    this.newFileInput = input;
    button.hidden = true;
    button.before(input);

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      this.newFileInput = null;
      input.remove();
      button.hidden = false;
    };
    const commit = (): boolean => {
      if (done) return true;
      const name = input.value.trim();
      if (name === from) {
        finish();
        return true;
      }
      const problem = this.#newFileProblem(name, rules);
      if (problem) {
        input.classList.add('invalid');
        input.title = problem;
        return false;
      }
      finish();
      void this.onRenameFile?.(from, name);
      return true;
    };

    input.onkeydown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        finish();
      } else {
        input.classList.remove('invalid');
      }
    };
    // Blur: keep a valid name, drop anything else — clicking away cancels.
    input.onblur = () => {
      if (!commit()) finish();
    };

    input.focus();
    input.select();
  }

  #showOrHideTabBar() {
    this.tabBar.hidden = this.models.size <= 1 && !this.allowNewFiles;
    window.dispatchEvent(new Event('reflowPlease'));
  }

  // Show the empty-editor placeholder instead of the editor until the first
  // file exists (only on new-file pages, which have an emptyState). Monaco
  // must re-measure when it comes back from display:none.
  #showOrHideEditor() {
    if (!this.emptyState) return;
    const empty = this.models.size === 0;
    this.editorDiv.hidden = empty;
    this.emptyState.hidden = !empty;
    if (!empty) this.editor.layout();
  }
}

const editorTabs = (editorId: string, config: TabsConfig) => new EditorTabs(editorId, config);

export default editorTabs;
